/**
 * `TransferAction` — the registered `React.FC<SlotProps>` container for the Transfer
 * action: renders the icon-only trigger, and owns everything `TransferModal` (presentational) does
 * not — reading `api` from `useCodingScreen()`, deriving the "Logged As" identity and the recipient
 * / dictionary data from the bound slot `value`, the open/close + submission state machine, and the
 * trigger ref the modal restores focus to. Mirrors `HoldAction` (CDMX-18412).
 *
 * ONE BOUND VALUE. A slot has exactly one `contentId`, so the modal's two data needs (who is acting,
 * and what may be picked) arrive together as `TransferActionData` at `contentId: 'case.transfer'` —
 * not as two bindings, and never by reaching into another slot's data. Binding under `case.*` also
 * makes the slot coding-only mechanically: `validateConfig` rejects `case.*` in a grading config.
 *
 * NO WRITE EXISTS. `api.transferCase` is optional and is currently implemented by nobody: the
 * available reassignment path cannot persist reasons/note/case-context and no transfer audit record
 * exists (ticket AC11/AC12). While it is absent the modal renders its preview notice and a disabled
 * primary action, so this container's submit path is unreachable in the app today — it is exercised
 * by unit tests and stories, and becomes live the moment an adapter supplies the capability.
 *
 * PERMISSION. `data.permission` is honoured when it is explicitly `'denied'`. An ABSENT permission
 * does not hide the trigger: nothing supplies it yet, and fail-closed-by-absence would make the
 * trigger invisible everywhere (ticket AC1). That is safe only because no write path exists — when
 * one lands, requiring `'allowed'` is a blocking precondition on that ticket.
 */
import { Box, Button } from '@chakra-ui/react';
import React, { useRef, useState } from 'react';

import { useCodingScreen } from '../../interface/context';
import type { SlotProps } from '../../interface/slotProps';
import type {
  TransferActionData,
  TransferPayload
} from '../../interface/transfer';
import { CASE_ACTION_RADIUS, TRANSFER_COLORS } from '../../styles/tokens';
import { TransferModal, type TransferSubmitState } from '../TransferModal';

const TRANSFER_LABEL = 'Transfer';

/** Narrow the slot `value` (unknown) to TransferActionData: only a genuine non-null non-array
 *  object; everything else (undefined/null/array/primitive) -> {} so the modal falls back to its
 *  own defaults and this component never throws. Same guard shape as `HoldAction`. */
const asTransferData = (value: unknown): TransferActionData =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as TransferActionData)
    : {};

const nameOf = (data: TransferActionData): string => {
  const displayName = data.currentUser?.displayName;
  return typeof displayName === 'string' && displayName.trim().length > 0
    ? displayName
    : 'XXX'; // placeholder until a real current user is injected (same as HoldAction)
};

export const TransferAction: React.FC<SlotProps> = ({ value }) => {
  const { api } = useCodingScreen();
  const data = asTransferData(value);
  const [isOpen, setIsOpen] = useState(false);
  const [loggedAs, setLoggedAs] = useState<{ name: string; at: Date } | null>(
    null
  );
  const [submitState, setSubmitState] = useState<TransferSubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Bumped per open and used as the modal's `key` -- remounts it fresh each time, so no stale
  // recipient/reason/validation state can survive a close->open.
  const [openGen, setOpenGen] = useState(0);

  if (data.permission === 'denied') return null;

  const open = (): void => {
    setLoggedAs({ name: nameOf(data), at: new Date() }); // frozen snapshot per open
    setSubmitState('idle');
    setErrorMessage(undefined);
    setOpenGen((g) => g + 1);
    setIsOpen(true);
  };
  const close = (): void => {
    setIsOpen(false);
    setSubmitState('idle');
    setErrorMessage(undefined);
  };

  const onSubmit = async (payload: TransferPayload): Promise<void> => {
    setErrorMessage(undefined);
    // Defensive: the modal disables its primary when this capability is missing, so this branch is
    // unreachable from the UI today. It must still never silently "succeed" and close -- the shell
    // is live-mounted, and a close would read as a completed transfer.
    if (!api?.transferCase) {
      setSubmitState('error');
      setErrorMessage(
        "Transfer isn't available yet — nothing was saved and the case was not moved."
      );
      return;
    }
    try {
      setSubmitState('transferring');
      await api.transferCase(payload);
    } catch {
      setSubmitState('error');
      setErrorMessage("Couldn't transfer this case. Try again.");
      return;
    }
    close();
  };

  return (
    <>
      <Button
        ref={triggerRef}
        data-testid="transfer-icon"
        aria-label={TRANSFER_LABEL}
        onClick={open}
        variant="unstyled"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        minW="auto"
        h="auto"
        p={0}
      >
        <Box
          aria-hidden
          bg={TRANSFER_COLORS.iconBg}
          borderRadius={CASE_ACTION_RADIUS.control}
          w="28px"
          h="28px"
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {/* North-east arrow, drawn inline: no allow-listed icon atom carries one, and the
              boundary guard forbids reaching for another package. Same call as HoldAction's
              hand-drawn pause glyph. */}
          <Box
            as="svg"
            viewBox="0 0 24 24"
            width="15px"
            height="15px"
            fill="none"
            stroke={TRANSFER_COLORS.iconArrow}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 17 17 7M9 7h8v8" />
          </Box>
        </Box>
      </Button>
      {loggedAs && (
        <TransferModal
          key={openGen}
          isOpen={isOpen}
          onClose={close}
          loggedAs={loggedAs}
          data={data}
          onSearch={api?.searchTransferRecipients}
          onSubmit={onSubmit}
          submitState={submitState}
          errorMessage={errorMessage}
          canSubmit={Boolean(api?.transferCase)}
          finalFocusRef={triggerRef}
        />
      )}
    </>
  );
};
